import React, { useEffect, useRef, useState } from "react";

import styled from "veneer/styled";

import { SpotPreview } from "cortex/ui/src/fleet/view/media-library/component/SpotPreview";
import type { Media } from "cortex/ui/src/fleet/view/media-library/model";

export type PreviewMediaEntry = Pick<
  Partial<Media>,
  "mimeType" | "numberOfSpots" | "type" | "url"
>;

interface ContentGroupPreviewBarProps {
  index?: number;
  mediaEntries?: ReadonlyArray<PreviewMediaEntry>;
  navigateTo?: (index: number) => void;
}

const ContentGroupPreviewBarRoot = styled("div")({
  display: "inline-flex",
  flexFlow: "row nowrap",
  transition: "transform 150ms ease-out",
});

const ContentGroupPreviewItem = styled("div")({
  cursor: "pointer",
  flexShrink: 0,
  "& .spot-preview": {
    border: "2px solid transparent",
    borderLeftWidth: 1,
    borderRadius: 0,
    borderRightWidth: 1,
    height: 50,
  },
  "&.active .spot-preview": {
    animation: "selected 1s infinite",
    animationTimingFunction: "ease-out",
  },
});

function calcScrollPosition(
  panel: Pick<HTMLElement, "clientWidth" | "offsetLeft"> | undefined,
  index: number,
) {
  const borderAdjust = index * 1;
  const sidebarMidpoint = 480 / 2;
  const offsetLeft = panel?.offsetLeft ?? 0;
  const clientWidth = panel?.clientWidth ?? 0;

  return (offsetLeft + clientWidth / 2 - sidebarMidpoint + borderAdjust) * -1;
}

export function ContentGroupPreviewBar({
  index = 0,
  mediaEntries = [],
  navigateTo = () => {},
}: ContentGroupPreviewBarProps) {
  const panelsRef = useRef<Record<number, HTMLDivElement | null>>({});
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    panelsRef.current = {};
    setInitialized(false);
  }, [mediaEntries]);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
    }
  }, [initialized]);

  const x = calcScrollPosition(panelsRef.current[index] ?? undefined, index);

  return (
    <ContentGroupPreviewBarRoot
      data-testid="content-group-preview-bar"
      style={{ transform: `translate(${x}px)` }}
    >
      {mediaEntries.map((mediaEntry, i) => {
        const { mimeType, numberOfSpots, type, url } = mediaEntry;
        const isActive = i === index ? " active" : "";

        return (
          <ContentGroupPreviewItem
            className={isActive}
            data-testid="content-group-preview-item"
            key={i}
            onClick={() => {
              navigateTo(i);
            }}
            ref={(elem) => {
              panelsRef.current[i] = elem;
            }}
          >
            <SpotPreview
              mimeType={mimeType ?? undefined}
              numberOfSpots={numberOfSpots ?? undefined}
              showControls={false}
              type={type ?? undefined}
              url={url ?? undefined}
            />
          </ContentGroupPreviewItem>
        );
      })}
    </ContentGroupPreviewBarRoot>
  );
}
